
import React, { useState } from 'react';
import { Search, MapPin } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLanguage } from '@/contexts/language';
import { chiapas_cities } from '@/data/chiapas/cities-data';
import { hidalgo_cities } from '@/data/hidalgo/cities-data';
import { useIsMobile } from '@/hooks/use-mobile';

interface SpecialRegionsDropdownProps {
  onCitySelect: (cityId: string) => void;
  isChiapasActive?: boolean;
  isHidalgoActive?: boolean;
}

const SpecialRegionsDropdown: React.FC<SpecialRegionsDropdownProps> = ({
  onCitySelect,
  isChiapasActive,
  isHidalgoActive
}) => {
  const { t } = useLanguage();
  const { isMobile } = useIsMobile();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Filter cities based on search term
  const filteredChiapasCities = chiapas_cities.filter(city =>
    city.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredHidalgoCities = hidalgo_cities.filter(city =>
    city.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const shouldShowChiapas = !isHidalgoActive || (searchTerm && filteredChiapasCities.length > 0);
  const shouldShowHidalgo = !isChiapasActive || (searchTerm && filteredHidalgoCities.length > 0);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className="w-[250px] justify-start bg-white/90 backdrop-blur-sm border-input truncate"
        >
          <MapPin className="mr-2 h-4 w-4 flex-shrink-0" />
          <span className="truncate">{t('searchByCity')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="w-[calc(100vw-32px)] sm:w-[300px] max-w-[300px]" 
        align="start"
        side={isMobile ? "bottom" : "bottom"}
        sideOffset={isMobile ? 4 : 6}
      >
        <div className="p-2">
          <Input
            placeholder={t('searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mb-2"
            autoFocus
          />
        </div>
        <ScrollArea className="h-[300px] max-h-[50vh]">
          {shouldShowChiapas && (
            <DropdownMenuGroup>
              <DropdownMenuLabel className="font-bold text-purple-700">
                Chiapas
              </DropdownMenuLabel>
              {filteredChiapasCities.length > 0 ? (
                filteredChiapasCities.map((city) => (
                  <DropdownMenuItem
                    key={city.city_id}
                    onClick={() => {
                      onCitySelect(city.city_id);
                      setIsOpen(false);
                      setSearchTerm('');
                    }}
                    className="flex items-center gap-2 pl-6"
                  >
                    <div 
                      className={`h-2 w-2 rounded-full flex-shrink-0 ${
                        city.church_presence === 'High' ? 'bg-green-500' :
                        city.church_presence === 'Medium' ? 'bg-amber-500' :
                        city.church_presence === 'Low' ? 'bg-orange-500' :
                        'bg-red-500'
                      }`}
                    />
                    <span className="truncate">{city.name}</span>
                  </DropdownMenuItem>
                ))
              ) : searchTerm && (
                <div className="px-2 py-1 text-sm text-muted-foreground">
                  {t('noResults')}
                </div>
              )}
              {shouldShowHidalgo && <DropdownMenuSeparator />}
            </DropdownMenuGroup>
          )}

          {shouldShowHidalgo && (
            <DropdownMenuGroup>
              <DropdownMenuLabel className="font-bold text-orange-700">
                Hidalgo
              </DropdownMenuLabel>
              {filteredHidalgoCities.length > 0 ? (
                filteredHidalgoCities.map((city) => (
                  <DropdownMenuItem
                    key={city.city_id}
                    onClick={() => {
                      onCitySelect(city.city_id);
                      setIsOpen(false);
                      setSearchTerm('');
                    }}
                    className="flex items-center gap-2 pl-6"
                  >
                    <div 
                      className={`h-2 w-2 rounded-full flex-shrink-0 ${
                        city.church_presence === 'High' ? 'bg-green-500' :
                        city.church_presence === 'Medium' ? 'bg-amber-500' :
                        city.church_presence === 'Low' ? 'bg-orange-500' :
                        'bg-red-500'
                      }`}
                    />
                    <span className="truncate">{city.name}</span>
                  </DropdownMenuItem>
                ))
              ) : searchTerm && (
                <div className="px-2 py-1 text-sm text-muted-foreground">
                  {t('noResults')}
                </div>
              )}
            </DropdownMenuGroup>
          )}

          {!filteredChiapasCities.length && !filteredHidalgoCities.length && !searchTerm && (
            <div className="p-2 text-sm text-muted-foreground text-center">
              {t('searchByCity')}
            </div>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default SpecialRegionsDropdown;
